import "./Documents.scss"
import noPicturesImg from "assets/img/no-documents.svg"

import React, { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { DragDropContext, Droppable } from "@hello-pangea/dnd"
import cn from "classnames"
import dayjs from "utils/dayjs"

import Text from "components/Text/Text"
import Select from "components/Select/Select"
import { Flex } from "components/Flex"
import { IApiLink, Guid } from "@/types"
import Button from "components/Button/Button"
import Spinner from "components/Spinner/Spinner"
import useDocuments from "hooks/useDocuments"
import AddDocumentsModal from "components/AddDocumentsModal/AddDocumentsModal"
import { ApiLinkEntity } from "config/constants"
import useReservationDocuments from "components/ReservationDocuments/hooks/useReservationDocuments"
import ReservationDocumentsCategory from "components/ReservationDocuments/ReservationDocumentsCategory"
import ReservationDocumentSelectCategoryModal from "components/ReservationDocuments/ReservationDocumentSelectCategoryModal"
import DroppableArea from "components/DroppableArea/DroppableArea"
import { Document } from "models/Document"
import AssetService from "services/AssetService"
import FileService from "services/FileService"
import toastMessages from "stores/toastMessages"

interface IDocumentsProps {
    entity: ApiLinkEntity
    linkGetDocuments?: IApiLink
    linkAddDocuments?: IApiLink
    withEmails?: boolean
    zipFileNameSuffix?: string
    hideTitle?: boolean
}

/**
 * Base documents component providing category-filtered document management with drag-and-drop
 * category reassignment, bulk ZIP download, and document deletion.
 */
const Documents: FC<React.PropsWithChildren<IDocumentsProps>> = ({
    entity,
    linkGetDocuments,
    linkAddDocuments,
    withEmails,
    zipFileNameSuffix,
    hideTitle = false,
}) => {
    const { t } = useTranslation()
    const [showAddDocumentsModal, setShowAddDocumentsModal] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<Guid | "0">("0")
    const [documentToChangeCategory, setDocumentToChangeCategory] = useState<{ sourceCategoryId: Guid, document: Document } | null>(null)
    const [isChangingDocuments, setIsChangingDocuments] = useState(false)
    const [isDownloadingAllDocuments, setIsDownloadingAllDocuments] = useState(false)

    const {
        hasDocuments,
        documentsByCategory,
        documentCategoryFilterOptions,
        documentCategorySelectOptions,
        fileIds,
        uploadDocuments,
        refetchDocuments,
        moveDocumentToCategory,
    } = useDocuments({
        entity,
        documentCategoryId: selectedCategory === "0" ? undefined : selectedCategory,
        withEmails,
        linkGetDocuments,
        linkAddDocuments,
    })

    const {
        onDragStart,
        onDragEnd,
        isDragging,
    } = useReservationDocuments({
        documentsByCategory: documentsByCategory || [],
        onDropToSelectNewCategory: setDocumentToChangeCategory,
        onDocumentCategoryChange: handleDocumentCategoryChange,
    })

    useEffect(() => {
        if (selectedCategory && !documentCategoryFilterOptions?.find(o => o.value === selectedCategory)) {
            setSelectedCategory("0")
        }
    }, [documentCategoryFilterOptions, selectedCategory])

    function renderAddDocumentsButton() {
        if (!linkAddDocuments) return null
        return (
            <Button
                theme="secondary"
                label={t('asset.addDocuments')}
                onClick={() => setShowAddDocumentsModal(true)}
                disabled={isChangingDocuments}
            />
        )
    }

    async function handleDocumentCategoryChange({ document, sourceCategoryId, destCategoryId }: { document: Document, sourceCategoryId: Guid, destCategoryId: Guid }) {
        if (!document.linkUpdateDocument) return

        setIsChangingDocuments(true)
        moveDocumentToCategory(document, destCategoryId)
        try {
            await AssetService.updateDocument(document.linkUpdateDocument, { documentCategoryId: destCategoryId })
        } catch (e) {
            moveDocumentToCategory(document, sourceCategoryId)
            setIsChangingDocuments(false)
            throw e
        }
        await refetchDocuments()
        setIsChangingDocuments(false)
    }

    async function handleDocumentDelete(link: IApiLink) {
        setIsChangingDocuments(true)
        await AssetService.deleteDocument(link)
        await refetchDocuments()
        setIsChangingDocuments(false)
    }

    async function handleDownloadAllDocuments() {
        if (!fileIds.length) return
        setIsDownloadingAllDocuments(true)
        try {
            const fileNameWithoutExtension = [
                t("documents.allDocumentsZIPFilePrefix"),
                dayjs().format("YYYY-MM-DD"),
                zipFileNameSuffix,
            ].filter(Boolean).join("_")
            await FileService.downloadZip({
                fileNameWithoutExtension,
                fileIds,
            })
            toastMessages.show({
                title: t("documents.successfullyDownloadedToast.title"),
                descr: `${fileNameWithoutExtension}.zip`,
            })
        } catch (e) {
            console.error(e)
            toastMessages.show({
                title: t("documents.downloadFailedToast.title"),
                descr: t("documents.downloadFailedToast.descr"),
                type: "error",
            })
        } finally {
            setIsDownloadingAllDocuments(false)
        }
    }

    const addDocumentsButton = renderAddDocumentsButton()
    const notReservedCategories = documentsByCategory?.filter(dc => !dc.isReserved && (selectedCategory === "0" ? true : dc.id === selectedCategory))
    const reservedCategories = documentsByCategory?.filter(dc => dc.isReserved && (selectedCategory === "0" ? true : dc.id === selectedCategory))

    return (
        <>
            <DragDropContext
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}>
                {(selectedCategory !== "0" || hasDocuments)
                    ? <div className="documents__header">
                        <div className={cn(
                            "documents__header-controls",
                            { "documents__header-controls--hidden": isDragging }
                        )}>
                            {(!hideTitle || fileIds.length > 0) &&
                                <Flex alignItems="center" justifyContent="space-between" className="mb-3" gap="2.4rem">
                                    {!hideTitle && <Text type="h3">{t("common.documents")}</Text>}
                                    {fileIds.length > 0 &&
                                        <Button
                                            theme="link"
                                            label={t('documents.downloadAllDocumentsAsZIP')}
                                            onClick={handleDownloadAllDocuments}
                                            disabled={isDownloadingAllDocuments}
                                            loading={isDownloadingAllDocuments}
                                        />
                                    }
                                </Flex>
                            }
                            <Flex justifyContent="space-between" alignItems="center">
                                {addDocumentsButton}
                                {!!documentCategoryFilterOptions && documentCategoryFilterOptions.length > 0 &&
                                    <Select
                                        value={selectedCategory}
                                        options={[
                                            { label: t("common.allCategories"), value: "0" },
                                            ...documentCategoryFilterOptions,
                                        ]}
                                        onChange={option => setSelectedCategory(option?.value || "0")}
                                    />
                                }
                            </Flex>
                        </div>
                        <div className={cn(
                            "documents__change-category-drop-area",
                            { "documents__change-category-drop-area--hidden": !isDragging }
                        )}>
                            <Droppable droppableId="changeCategory">
                                {(provided, snapshot) =>
                                    <div {...provided.droppableProps}
                                        ref={provided.innerRef}>
                                        <DroppableArea isDragOver={snapshot.isDraggingOver}>
                                            <Flex alignItems="center" justifyContent="center" className="h-100 px-4 text-center">
                                                {t("documents.dropHereToSelectNewCategory")}
                                            </Flex>
                                        </DroppableArea>
                                    </div>
                                }
                            </Droppable>
                        </div>
                    </div>
                    : (!hideTitle && <Text type="h3" className="mb-3">{t("common.documents")}</Text>)
                }
                {documentsByCategory &&
                    <>
                        {!!notReservedCategories?.length && !!hasDocuments && notReservedCategories.map(category => (
                            <ReservationDocumentsCategory
                                key={category.id}
                                category={category}
                                disabled={isChangingDocuments}
                                onDocumentDelete={handleDocumentDelete}
                            />
                        ))}
                        {selectedCategory === "0" && !hasDocuments &&
                            <Flex flow="column" className="text-center py-4" alignItems="center" gap="3.2rem">
                                <img src={noPicturesImg} alt="" />
                                <Text type="p" className="white-space-pre-line">{t('documents.noDocumentsUploadedYet')}</Text>
                                {addDocumentsButton}
                            </Flex>
                        }
                        {!!reservedCategories?.length && !!hasDocuments &&
                            <>
                                <Text type="h3" className="mt-4">{t('documents.machineParkDocuments')}</Text>
                                {reservedCategories.map(category => (
                                    <ReservationDocumentsCategory
                                        key={category.id}
                                        category={category}
                                        disabled={isChangingDocuments}
                                        onDocumentDelete={handleDocumentDelete}
                                    />
                                ))}
                            </>
                        }
                    </>
                }
                {!documentsByCategory && <Spinner className="py-8" />}
                {showAddDocumentsModal &&
                    <AddDocumentsModal
                        categories={documentCategorySelectOptions}
                        categoryRequired
                        onSave={uploadDocuments}
                        onClose={() => setShowAddDocumentsModal(false)}
                    />
                }
            </DragDropContext>
            {!!documentToChangeCategory &&
                <ReservationDocumentSelectCategoryModal
                    categoies={documentCategorySelectOptions.filter(o => o.value !== documentToChangeCategory.sourceCategoryId)}
                    onSelect={documentCategoryId => documentToChangeCategory && handleDocumentCategoryChange({ document: documentToChangeCategory.document, sourceCategoryId: documentToChangeCategory.sourceCategoryId, destCategoryId: documentCategoryId })}
                    onClose={() => setDocumentToChangeCategory(null)}
                />
            }
        </>
    )
}

export default Documents
